import React, { useCallback, useRef } from "react";
import { withRouter, useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { RouteComponentProps } from "react-router";
import AppBar from "@material-ui/core/AppBar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Button from "@material-ui/core/Button";
import classNames from "classnames";
import { StateService } from "../services/StateService";
import { observer } from "mobx-react-lite";
import { Toast, ToastProps } from "./Toast";
import logo from '../assets/nft-logo.svg';

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const appBarFontSize = 23;

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: "transparent",
    maxWidth: 1400,
    position: "relative",
    margin: "0 auto",
  },
  appBar: {
    backgroundColor: "transparent",
    boxShadow: "none",
    '&::before': {
    border: "1px solid #BEC5F8",
    content: `''`,
    position: 'absolute',
    width: '100%',
    bottom: '4px',
    opacity: 0.2,
    }
  },
  appBarContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  indicator: {
    height: "5px",
    borderRadius: "5px",
    backgroundImage: "linear-gradient(45deg, #3457F2, #B139D8)",
  },
  appLogo: {
    width: "12%",
    position: "absolute",
    left: "14px",
    top: "40%",
  },
  tab: {
    fontSize: appBarFontSize,
    color: "#fff",
    padding: "25px",
    fontFamily: "Gilroy-Medium, sans-serif",
    textTransform: "initial",
  },
  tabActive: {
    fontSize: appBarFontSize,
    color: "#4e37de",
    padding: "25px",
    fontFamily: "Gilroy-Medium, sans-serif",
    textTransform: "initial",
    background: "rgba(33, 35, 40, 0.4)",
    boxShadow: "0px 67px 79px rgb(0 0 0 / 30%)",
    borderRadius: "0px 0px 8px 8px",
  },
  tabInactive: {
    fontSize: appBarFontSize,
    color: "#808080",
    padding: "25px",
    fontFamily: "Gilroy-Medium, sans-serif",
    textTransform: "initial",
  },
  button: {
    right: "3px",
    position: "absolute",
    maxWidth: 350,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    fontFamily: "Gilroy-Medium, sans-serif",
  },
  buttonConnect: {
    backgroundColor: "#4e37de",
    fontFamily: 'Gilroy-Medium, sans-serif',
  },
  buttonLogOut: {
    fontFamily: 'Gilroy-Medium, sans-serif',
    backgroundColor: "#4e37de",
    position: "relative",
    float: "left",
    marginRight: "20px"
  }
}));

const tabs = [
  { link: "/", title: "About" },
  { link: "/auction", title: "Auction" },
  { link: "/build", title: "Build" },
  { link: "/vote", title: "Vote" },
  { link: "/winners", title: "Winners" },
];

const getIndex = (link: string): number => {
  let index = 0;
  for (let i = 0; i < tabs.length; i++) {
    if (link === tabs[i].link) {
      index = i;
      break;
    }
  }
  return index;
};

type PathParamsType = {
  param1: string;
};

type AppHeaderProps = RouteComponentProps<PathParamsType> & {
  state: StateService;
};

// Function to return the tab index for the current round
function getTabIndex(current_round: string): number {

  let index = 0;

  switch (current_round) {
    case "auction":
      index = 1;
      break;
    case "entropy":
      index = 2;
      break;
    case "voting1":
    case "voting2":
    case "voting3":
      index = 3;
      break;
    case "done":
      index = 4;
      break;
  }

  return index;
}


export const AppHeader = withRouter(
  observer((props: AppHeaderProps) => {
    const classes = useStyles();
    let history = useHistory();
    let location = useLocation();
    const [value, setValue] = React.useState(getIndex(location.pathname));
    const tabIndex = getTabIndex(props.state.rounds.getActiveRound());
    const messageToast = useRef<ToastProps>(null);
    const [hasWithdrawn, setHasWithdrawn] = React.useState<boolean>(false);
    let withdrawAmount: number = 0;

    const address = props.state.authentication.getAddress();

    if (address !== "NONE") {
      const [ selfDeposit, selfStakeNeeded ] = props.state.bidStore.getSelfDepositAndStake();
      withdrawAmount = selfDeposit - selfStakeNeeded;
      }

    const tabTitle = tabs[value].title;
    const canWithdraw = props.state.authentication.getAddress() !== "NONE" && props.state.rounds.isDonePhase() && !hasWithdrawn && tabTitle === "Winners" && withdrawAmount > 0 && props.state.bidStore.isCleared();

    const handleChange = (event: unknown, newValue: number) => {
      if (newValue > tabIndex) {
        messageToast.current?.showMessage(`It's too early to access the ${tabs[newValue].title.toLowerCase()} page`, 'error');
        return;
      }

      setValue(newValue);
      history.push(tabs[newValue].link);
    };

    const connectWallet = useCallback(
      async (event) => {
        const [status, message] = await props.state.onClickLoginMeta(event);
        messageToast.current?.showMessage(message as string, status ? 'success' : 'error');
      },
      [props.state]
    );

    const withdraw = async (event: any) => {
      event.preventDefault();
      setHasWithdrawn(true);
      try {
        let address = props.state.authentication.getAddress();
        if (address === "NONE" || !props.state.web3.isAvailable()) {
          messageToast.current?.showMessage("Login first!", 'error');
          return;
        }
        let status = await props.state.web3.getContract()?.withdrawSelfStake(address);
        if (status) {
          messageToast.current?.showMessage("Successful withdraw!", 'info');
        } else {
          messageToast.current?.showMessage("Failed to withdraw!", 'error');
        }
      } catch (exception) {
        console.log("Withdraw failed: ", exception);
        messageToast.current?.showMessage("Failed to withdraw!", 'error');
      }
    };

    return (
      <div className={classes.root}>
        <Toast ref={messageToast} />
        <AppBar position="relative" className={classes.appBar}>
          <img className={classes.appLogo} src={logo} alt="logo" />
          <div className={classes.appBarContent}>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label=""
              classes={{ indicator: classes.indicator }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  label={tab.title}
                  className={classNames({
                    [classes.tabActive]: location.pathname === tab.link,
                    [classes.tabInactive]: index > tabIndex,
                    [classes.tab]: location.pathname !== tab.link && !(index > tabIndex),
                  })}
                  {...a11yProps(index)}
                />
              ))}
            </Tabs>
            <div className={classes.button}>
              { canWithdraw && (
                <Button
                  className={classes.buttonConnect}
                  variant="contained"
                  color="primary"
                  onClick={withdraw}
                >
                  GET BID ({withdrawAmount} FET)
                </Button>
              )}
              {!canWithdraw && props.state.authentication.getAuthStatus() && (
                <Button
                  className={classes.buttonLogOut}
                  variant="contained"
                  color="primary"
                  onClick={props.state.onClickLogout}
                >
                  DISCONNECT WALLET
                </Button>
              )}
              {!canWithdraw && !props.state.authentication.getAuthStatus() && (
                <Button
                  className={classes.buttonConnect}
                  variant="contained"
                  color="primary"
                  onClick={connectWallet}
                >
                  CONNECT WALLET
                </Button>
              )}
            </div>
          </div>
        </AppBar>
      </div>
    );
  })
);
